import { TableFiltersGroup } from './TableFilterService';
import { DisplayMode, FilterItem, FilterPosition, Sorting } from './types';

export const FETCH_LIST_START = 'waldur/table/FETCH_START';
export const FETCH_LIST_DONE = 'waldur/table/FETCH_DONE';
export const FETCH_LIST_ERROR = 'waldur/table/FETCH_ERROR';
export const FETCH_LIST_GOTO_PAGE = 'waldur/table/GOTO_PAGE';
export const SET_MODE = 'waldur/table/SET_MODE';
export const SET_FILTER_QUERY = 'waldur/table/SET_QUERY';
export const RESET_PAGINATION = 'waldur/table/RESET_PAGINATION';
export const PAGE_SIZE_UPDATE = 'waldur/table/PAGE_SIZE_UPDATE';
export const SORT_LIST_START = 'waldur/table/SORT_LIST_START';
export const SORT_LIST_DONE = 'waldur/table/SORT_LIST_DONE';
export const SET_FILTER_POSITION = 'waldur/table/SET_FILTER_POSITION';
export const SET_FILTER = 'waldur/table/SET_FILTER';
export const SET_SAVED_FILTERS = 'waldur/table/SET_SAVED_FILTERS';
export const SELECT_SAVED_FILTER = 'waldur/table/SELECT_SAVED_FILTER';
export const APPLY_FILTERS = 'waldur/table/APPLY_FILTERS';
export const TOGGLE_ROW = 'waldur/table/TOGGLE_ROW';
export const SET_TOGGLED = 'waldur/table/SET_TOGGLED';
export const SELECT_ROW = 'waldur/table/SELECT_ROW';
export const SELECT_ALL_ROWS = 'waldur/table/SELECT_ALL_ROWS';
export const RESET_SELECTION = 'waldur/table/RESET_SELECTION';
export const TOGGLE_COLUMN = 'waldur/table/TOGGLE_COLUMN';
export const RESET_COLUMNS = 'waldur/table/RESET_COLUMNS';
export const INIT_COLUMN_POSITIONS = 'waldur/table/INIT_COLUMN_POSITIONS';
export const SWAP_COLUMNS = 'waldur/table/SWAP_COLUMNS';
export const TOGGLE_COLUMN_PIN = 'waldur/table/TOGGLE_COLUMN_PIN';
export const CLEAR_ALL_FILTERS = 'waldur/table/CLEAR_ALL_FILTERS';
export const RESTORE_FILTERS = 'waldur/table/RESTORE_FILTERS';
export const REGISTER_FILTER_NAME = 'waldur/table/REGISTER_FILTER_NAME';
export const CLEAR_REGISTERED_FILTER_NAMES =
  'waldur/table/CLEAR_REGISTERED_FILTER_NAMES';

export const fetchListDone = (table: string, resultCount: number) => ({
  type: FETCH_LIST_DONE,
  payload: {
    table,
    resultCount,
  },
});

export const fetchListError = (table: string, error: any) => ({
  type: FETCH_LIST_ERROR,
  payload: {
    table,
    error,
  },
});

export const fetchListGotoPage = (table: string, page: number) => ({
  type: FETCH_LIST_GOTO_PAGE,
  payload: {
    table,
    page,
  },
});

export const setMode = (table: string, mode: DisplayMode) => ({
  type: SET_MODE,
  payload: {
    table,
    mode,
  },
});

export const setFilterQuery = (table: string, query: string) => ({
  type: SET_FILTER_QUERY,
  payload: {
    table,
    query,
  },
});

export const resetPagination = (table: string) => ({
  type: RESET_PAGINATION,
  payload: {
    table,
  },
});

export const updatePageSize = (table: string, size: number) => ({
  type: PAGE_SIZE_UPDATE,
  payload: {
    table,
    size,
  },
});

export const sortListStart = (table: string, sorting: Sorting) => ({
  type: SORT_LIST_START,
  payload: {
    table,
    sorting,
  },
});

export const sortListDone = (table: string) => ({
  type: SORT_LIST_DONE,
  payload: {
    table,
  },
});

export const setFilterPosition = (
  table: string,
  filterPosition: FilterPosition,
) => ({
  type: SET_FILTER_POSITION,
  payload: {
    table,
    filterPosition,
  },
});

export const setFilter = (table: string, item: FilterItem) => ({
  type: SET_FILTER,
  payload: {
    table,
    item,
  },
});

export const setSavedFilters = (table: string, items: TableFiltersGroup[]) => ({
  type: SET_SAVED_FILTERS,
  payload: {
    table,
    items,
  },
});

export const selectSavedFilter = (table: string, item: TableFiltersGroup) => ({
  type: SELECT_SAVED_FILTER,
  payload: {
    table,
    item,
  },
});

export const applyFilters = (table: string, apply: boolean) => ({
  type: APPLY_FILTERS,
  payload: {
    table,
    apply,
  },
});

export const toggleRow = (table: string, row: string | number) => ({
  type: TOGGLE_ROW,
  payload: {
    table,
    row,
  },
});

export const setToggled = (
  table: string,
  toggled: Record<string, boolean>,
) => ({
  type: SET_TOGGLED,
  payload: { table, toggled },
});

export const selectRow = (table: string, row: any) => ({
  type: SELECT_ROW,
  payload: {
    table,
    row,
  },
});

export const selectAllRows = (table: string, rows: any[]) => ({
  type: SELECT_ALL_ROWS,
  payload: {
    table,
    rows,
  },
});

export const resetSelection = (table: string) => ({
  type: RESET_SELECTION,
  payload: {
    table,
  },
});

export const toggleColumn = (table: string, id, column, value?: boolean) => ({
  type: TOGGLE_COLUMN,
  payload: {
    table,
    id,
    column,
    value,
  },
});

export const resetColumns = (table: string) => ({
  type: RESET_COLUMNS,
  payload: {
    table,
  },
});

export const initColumnPositions = (
  table: string,
  columnPositions: string[],
) => ({
  type: INIT_COLUMN_POSITIONS,
  payload: {
    table,
    columnPositions,
  },
});

export const swapColumns = (
  table: string,
  column1: string,
  column2: string,
) => ({
  type: SWAP_COLUMNS,
  payload: {
    table,
    column1,
    column2,
  },
});

export const toggleColumnPin = (table: string, id: string) => ({
  type: TOGGLE_COLUMN_PIN,
  payload: {
    table,
    id,
  },
});

export const clearAllFilters = (table: string) => ({
  type: CLEAR_ALL_FILTERS,
  payload: { table },
});

/** Puts back the filters (and selected saved filter) a table had earlier, e.g.
 * when the filter panel is cancelled after its fields changed them. */
export const restoreFilters = (
  table: string,
  filtersStorage: FilterItem[],
  selectedSavedFilter: TableFiltersGroup | null,
) => ({
  type: RESTORE_FILTERS,
  payload: { table, filtersStorage, selectedSavedFilter },
});

/** Record that a filter field with the given `name` is rendered by this table.
 * Emitted by every leaf filter via `withTableFilter`. Lets the table tell its
 * own filter fields apart from unrelated global URL params. */
export const registerFilterName = (table: string, name: string) => ({
  type: REGISTER_FILTER_NAME,
  payload: { table, name },
});

/** Drop the registered filter names for a table. Dispatched when the table is
 * fully unmounted so a later mount (possibly a different view reusing the same
 * table id) rebuilds the set from the fields it actually renders. */
export const clearRegisteredFilterNames = (table: string) => ({
  type: CLEAR_REGISTERED_FILTER_NAMES,
  payload: { table },
});
